import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { featureTemplateService } from "@/services/featureTemplateService";
import { FeatureTemplateWithDetails } from "@/types/featureTemplate";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useToast } from "@/components/ui/use-toast";
import { ArrowLeft, Bot, FileJson, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { FeatureTemplateForm } from "@/components/feature-templates/FeatureTemplateForm";
import { useOrganization } from "@/App";

export default function FeatureTemplateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [template, setTemplate] = useState<FeatureTemplateWithDetails | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const { currentOrganization } = useOrganization();

  const fetchTemplate = async () => {
    if (!id) return;

    try {
      const data = await featureTemplateService.getFeatureTemplate(id);
      setTemplate(data);
    } catch (error) {
      console.error("Error fetching feature template:", error);
      toast({
        title: "Error",
        description: "Failed to load feature template details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!template || !currentOrganization) {
    return (
      <div className="text-center py-8">
        <p className="text-muted-foreground">Template not found</p>
      </div>
    );
  }

  const renderLimitBadge = (
    value: number | undefined | null,
    label: string,
  ) => {
    if (value === undefined || value === null) return null;
    return (
      <Badge variant="secondary" className="mr-2 rounded-none">
        {label}: {value}
      </Badge>
    );
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button
          variant="ghost"
          className="gap-2 rounded-none"
          onClick={() => navigate("/feature-templates")}
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Templates
        </Button>
      </div>

      <Tabs defaultValue="details" className="w-full">
        <TabsList className="rounded-none">
          <TabsTrigger value="details">Details</TabsTrigger>
          <TabsTrigger value="campaigns">Campaigns</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="details" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Basic Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h3 className="text-sm font-medium">Name</h3>
                <p className="text-sm text-muted-foreground">{template.name}</p>
              </div>

              <div>
                <h3 className="text-sm font-medium">Agent</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Bot className="h-4 w-4" />
                  <span>{template.assistant.name}</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium">Schema Template</h3>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <FileJson className="h-4 w-4" />
                  <span>{template.schema_template.name}</span>
                </div>
              </div>

              <div>
                <h3 className="text-sm font-medium">Created</h3>
                <p className="text-sm text-muted-foreground">
                  {format(new Date(template.created_at), "PPpp")}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Usage Limits</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {renderLimitBadge(template.max_instances, "Instances")}
                {renderLimitBadge(template.max_leads_per_instance, "Leads")}
                {renderLimitBadge(
                  template.max_call_minutes_per_instance,
                  "Minutes",
                )}
                {renderLimitBadge(template.max_reports_per_instance, "Reports")}
                {!template.max_instances &&
                  !template.max_leads_per_instance &&
                  !template.max_call_minutes_per_instance &&
                  !template.max_reports_per_instance && (
                    <Badge variant="outline" className="rounded-none">
                      No limits
                    </Badge>
                  )}
              </div>

              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Maximum Instances: {template.max_instances || "No limit"}</p>
                <p>
                  Maximum Leads per Instance:{" "}
                  {template.max_leads_per_instance || "No limit"}
                </p>
                <p>
                  Maximum Call Minutes per Instance:{" "}
                  {template.max_call_minutes_per_instance || "No limit"}
                </p>
                <p>
                  Maximum Reports per Instance:{" "}
                  {template.max_reports_per_instance || "No limit"}
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="campaigns">
          <Card>
            <CardHeader>
              <CardTitle>Campaigns</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                Campaign management coming soon...
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Template Settings</CardTitle>
            </CardHeader>
            <CardContent>
              <FeatureTemplateForm
                organizationId={currentOrganization.id}
                initialData={{
                  name: template.name,
                  agent_id: template.agent_id,
                  schema_template_id: template.schema_template_id,
                  max_instances: template.max_instances,
                  max_leads_per_instance: template.max_leads_per_instance,
                  max_call_minutes_per_instance:
                    template.max_call_minutes_per_instance,
                  max_reports_per_instance: template.max_reports_per_instance,
                }}
                onComplete={fetchTemplate}
                disableSchemaTemplate={true}
              />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
